/**
 * Context Menu Component
 * 
 * BIM 2025-style right-click context menu with submenu support.
 */

import { useEffect, useLayoutEffect, useRef, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useContextMenuStore } from '../../state/useContextMenuStore';
import { useCommandManager } from '../../state/commandStore';
import { useShallow } from 'zustand/react/shallow';
import {
    ContextMenuEntry,
    ContextMenuItem as MenuItemType,
    getContextMenuItems
} from '../../config/contextMenuConfig';
import { useAppStore } from '../../state/store';
import { useBIMStore } from '../../state/useBIMStore';
import { ChevronRight } from 'lucide-react';
import '../../styles/global.css';
import '../../styles/context-menu.css';

interface ContextMenuProps {
    // Optional override for menu items
    items?: ContextMenuEntry[];
}

export function ContextMenu({ items: propsItems }: ContextMenuProps) {
    // useShallow: the store hook is only re-rendered when one of the fields
    // actually used below changes (was a whole-store subscription).
    const { visible, position, hide, targetElementIds, targetElementId, customItems } =
        useContextMenuStore(useShallow(s => ({
            visible: s.visible,
            position: s.position,
            hide: s.hide,
            targetElementIds: s.targetElementIds,
            targetElementId: s.targetElementId,
            customItems: s.customItems,
        })));
    // Action-only reads use stable refs; `commands` stays subscribed because
    // menu items resolve their availability through it.
    const executeCommand = useCommandManager((s) => s.executeCommand);
    const commands = useCommandManager((s) => s.commands);

    const menuRef = useRef<HTMLDivElement>(null);
    const selectedIds = useAppStore(s => s.selectedIds);
    const activeTool = useBIMStore(s => s.activeTool);

    // Get appropriate menu items based on context. Memoized: the old direct
    // call produced a fresh array per render, which invalidated the `items`
    // memo below every time (the filter-tree traversal always re-ran).
    // Caller-supplied entries (customItems channel) win over the selection
    // config tree — the sketch toolbar's settings menu rides the same shell.
    const rawItems = useMemo(
        () => propsItems || customItems || getContextMenuItems(
            selectedIds.length > 0 ? selectedIds : targetElementIds,
            activeTool !== 'SELECT' ? activeTool : null,
            targetElementId || targetElementIds[0] || null
        ),
        [propsItems, customItems, selectedIds, targetElementIds, activeTool, targetElementId]
    );

    // Evaluate config-level conditions ('hasFamily', 'canFlip', 'isSurface')
    // against the current BIM selection so conditional items only appear when
    // they apply.
    const items = useMemo(() => {
        const bim = useBIMStore.getState();
        const ids = selectedIds.length > 0 ? selectedIds : targetElementIds;
        const elements = ids
            .map(id => bim.project.elements.get(id))
            .filter(Boolean) as Array<{ category: string; type: string }>;

        const appEntities = ids
            .map(id => useAppStore.getState().entities[id])
            .filter(Boolean) as Array<{ type: string }>;

        const evaluate = (condition?: string): boolean => {
            if (!condition) return true;
            switch (condition) {
                case 'hasFamily':
                    // Family-typed: BIM elements with a family type, or app
                    // entities (all carry a type)
                    return elements.length > 0 || appEntities.length > 0;
                case 'canFlip':
                    return elements.some(el =>
                        el.category === 'Doors' ||
                        el.category === 'Windows' ||
                        String(el.type).toLowerCase().includes('door') ||
                        String(el.type).toLowerCase().includes('window')
                    );
                case 'isSurface':
                    return elements.some(el =>
                        el.category === 'Walls' ||
                        el.category === 'Floors' ||
                        el.category === 'Ceilings' ||
                        el.category === 'Roofs'
                    );
                case 'isLinkedCad':
                    // Reload only applies to linked/imported CAD objects.
                    return elements.some(el =>
                        el.category === 'CAD Links' ||
                        String(el.type) === 'Imported CAD'
                    );
                default:
                    return true;
            }
        };

        const filterTree = (entries: typeof rawItems): typeof rawItems => {
            const out: typeof rawItems = [];
            for (const entry of entries) {
                if ('type' in entry) { out.push(entry); continue; }
                if (!evaluate(entry.condition)) continue;
                if (entry.submenu) {
                    const filteredSub = filterTree(entry.submenu as any);
                    if (filteredSub.length === 0) continue;
                    out.push({ ...entry, submenu: filteredSub as any });
                } else {
                    out.push(entry);
                }
            }
            return out;
        };

        return filterTree(rawItems);
    }, [rawItems, selectedIds, targetElementIds]);

    // Close on click outside
    useEffect(() => {
        if (!visible) return;

        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Element;
            // Submenus are portaled outside the main menu element, so any
            // element carrying the context-menu class counts as "inside".
            if (target?.closest?.('.context-menu')) return;
            hide();
        };

        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                hide();
            }
        };

        // Delay to prevent immediate close on same click
        const timer = setTimeout(() => {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleEscape);
        }, 10);

        return () => {
            clearTimeout(timer);
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [visible, hide]);

    // Handle action execution
    const executeAction = useCallback((actionName: string) => {
        // Try to execute via CommandStore first
        executeCommand(actionName);
        hide();
    }, [executeCommand, hide]);

    if (!visible) return null;

    return createPortal(
        <>
            {/* Invisible backdrop to capture clicks */}
            <div className="context-menu-backdrop" onClick={hide} />

            {/* Menu container */}
            <div
                ref={menuRef}
                className="context-menu"
                style={{
                    left: position.x,
                    top: position.y,
                }}
            >
                {items.map((item, index) => (
                    <ContextMenuItemRenderer
                        key={'type' in item ? `divider-${index}` : item.id}
                        item={item}
                        onExecute={executeAction}
                        onClose={hide}
                        commands={commands}
                    />
                ))}
            </div>
        </>,
        document.body
    );
}

interface ContextMenuItemRendererProps {
    item: ContextMenuEntry;
    onExecute: (action: string) => void;
    onClose: () => void;
    commands: Record<string, any>;
}

function ContextMenuItemRenderer({ item, onExecute, onClose, commands }: ContextMenuItemRendererProps) {
    const [showSubmenu, setShowSubmenu] = useState(false);
    const itemRef = useRef<HTMLDivElement>(null);
    const submenuRef = useRef<HTMLDivElement>(null);
    const submenuTimeoutRef = useRef<number | null>(null);
    // Fixed viewport coordinates for the portaled submenu
    const [submenuCoords, setSubmenuCoords] = useState<{ left: number; top: number } | null>(null);

    const isDivider = 'type' in item && item.type === 'divider';
    const hasSubmenu = !isDivider && !!(item as MenuItemType).submenu?.length;
    const isOpen = hasSubmenu && showSubmenu;

    useEffect(() => {
        return () => {
            if (submenuTimeoutRef.current) {
                clearTimeout(submenuTimeoutRef.current);
            }
        };
    }, []);

    // Compute submenu placement against the real viewport while it is open.
    // Portaling to document.body escapes any overflow clipping / stacking
    // issues from the parent menu, and the flip logic keeps it fully visible.
    useLayoutEffect(() => {
        if (!isOpen) {
            setSubmenuCoords(null);
            return;
        }
        const place = () => {
            const itemRect = itemRef.current?.getBoundingClientRect();
            if (!itemRect) return;
            // Live-measure the submenu when it exists; fall back to the
            // minimum menu width for the very first placement pass.
            const subRect = submenuRef.current?.getBoundingClientRect();
            const subWidth = subRect && subRect.width > 0 ? subRect.width : 220;
            const subHeight = subRect?.height ?? 0;
            const gap = 2;
            const margin = 8;

            // Horizontal: open to the right unless it would overflow, then flip left
            let left = itemRect.right + gap;
            if (left + subWidth + margin > window.innerWidth) {
                left = itemRect.left - subWidth - gap;
            }
            if (left < margin) {
                left = Math.min(margin, window.innerWidth - subWidth - margin);
            }

            // Vertical: keep inside viewport
            let top = itemRect.top - 4;
            if (subHeight > 0 && top + subHeight + margin > window.innerHeight) {
                top = Math.max(margin, window.innerHeight - subHeight - margin);
            }

            if (!Number.isFinite(left) || !Number.isFinite(top)) return;
            setSubmenuCoords(prev =>
                prev && prev.left === left && prev.top === top ? prev : { left, top }
            );
        };

        place();
        // One refinement pass after the portal has painted, so placement uses
        // the submenu's real dimensions. Re-running is keyed only on isOpen to
        // avoid a measure->setState->measure feedback loop.
        const raf = requestAnimationFrame(place);
        window.addEventListener('resize', place);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('resize', place);
        };
    }, [isOpen]);

    if (isDivider) {
        return <div className="context-menu-divider" />;
    }

    const menuItem = item as MenuItemType;

    const handleMouseEnter = () => {
        if (hasSubmenu) {
            // Clear any pending timeout
            if (submenuTimeoutRef.current) {
                clearTimeout(submenuTimeoutRef.current);
            }
            // Placement is computed in the layout effect once the submenu mounts
            setShowSubmenu(true);
        }
    };

    const handleMouseLeave = () => {
        if (hasSubmenu) {
            // Delay hiding to allow mouse to move to submenu
            submenuTimeoutRef.current = window.setTimeout(() => {
                setShowSubmenu(false);
            }, 150);
        }
    };

    const handleClick = () => {
        if (menuItem.disabled) return;

        if (hasSubmenu) {
            // Toggle submenu on click (for accessibility)
            setShowSubmenu(!showSubmenu);
        } else if (menuItem.action) {
            onExecute(menuItem.action);
        }
    };

    return (
        <div
            ref={itemRef}
            className={`context-menu-item ${menuItem.disabled ? 'context-menu-item--disabled' : ''} ${hasSubmenu ? 'context-menu-submenu' : ''}`}
            onClick={handleClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <div className="context-menu-item__content">
                <span className="context-menu-item__icon">
                    {/* Icon placeholder - can add icon mapping later */}
                </span>
                <span className="context-menu-item__label">{menuItem.label}</span>
            </div>

            {/* Show shortcut from command store if available, otherwise fallback to item.shortcut */}
            {(commands[menuItem.action!]?.shortcut || menuItem.shortcut) && (
                <span className="context-menu-item__shortcut">
                    {commands[menuItem.action!]?.shortcut || menuItem.shortcut}
                </span>
            )}

            {hasSubmenu && (
                <ChevronRight className="context-menu-item__arrow" size={12} />
            )}

            {/* Submenu — portaled to body so the parent menu's overflow:hidden
                never clips it, positioned with fixed viewport coordinates */}
            {isOpen && submenuCoords && createPortal(
                <div
                    ref={submenuRef}
                    className="context-menu context-menu-submenu__menu"
                    style={{
                        left: submenuCoords.left,
                        top: submenuCoords.top,
                    }}
                    onMouseEnter={() => {
                        if (submenuTimeoutRef.current) {
                            clearTimeout(submenuTimeoutRef.current);
                        }
                    }}
                    onMouseLeave={handleMouseLeave}
                >
                    {menuItem.submenu!.map((subItem, index) => (
                        <ContextMenuItemRenderer
                            key={'type' in subItem ? `divider-${index}` : subItem.id}
                            item={subItem}
                            onExecute={onExecute}
                            onClose={onClose}
                            commands={commands}
                        />
                    ))}
                </div>,
                document.body
            )}
        </div>
    );
}

export default ContextMenu;
